"use client";

import Image from "next/image";
import { CarouselContentVisible, CarouselItem } from "~/components/ui/carousel";
import Badge from "~/componentsDS/Badge";
import { QuizCTA } from "~/components/features/quiz";
import Carousel from "~/componentsDS/Carousel";
import PulseInfoButton from "~/componentsDS/PulseInfoButton";
import Reveal from "~/componentsDS/Reveal";
import Heading from "~/componentsDS/Typography/Heading";
import { QUIZ_ROUTES } from "~/constants/quizRoutes";
import { cn } from "~/lib/utils";
import ElementorContainer from "~/componentsDS/ElementorLoyout/ElementorContainer";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import HormoneCard from "~/componentsDS/HormoneCard";
import { scienceHormones } from "~/componentsDS/LPComponents/data";
import BlackBox from "~/componentsDS/BlackBox";
import useLpCtaText from "~/hooks/useLpCtaText";
import TextWithRegisteredMark from "~/componentsDS/TextWithRegisteredMark";

const blackBoxEventData = {
    event: "user_interaction",
    value: {
        event_name: GTM_EVENT_NAMES.LP_INTERACTION,
        widget: "lp_fda_announcement",
        meta_data: {
            click_text: "More info"
        },
    },
};

type Props = {
    creamImageAlt?: string;
    className?: string;
    showFdaBlackBox?: boolean;
    buttonWrapperClassName?: string;
    tooltipVariant?: "default" | "white";
};

const CTA_TITLE = "See If Oestra® Is Right for You";

const hormoneCardEventData = {
    event_name: GTM_EVENT_NAMES.LP_INTERACTION,
    widget: "our_science",
};

const HormonesSection = ({
    creamImageAlt = "Oestra® 3-in-1 bioidentical HRT cream by Inner Balance",
    className = "pt-6 md:pt-[96px] pb-4",
    showFdaBlackBox = false,
    buttonWrapperClassName = "flex justify-center px-6 mt-8",
    tooltipVariant = "default",
}: Props) => {

    const ctaTitle = useLpCtaText(CTA_TITLE);

    return (
        <section className={className}>
            <ElementorContainer innerClassName="flex flex-col">
                <Reveal className="text-primary500">
                    {showFdaBlackBox && (
                        <BlackBox className="md:hidden mb-6 bg-primary100 text-primary500 p-4 rounded-2xl" eventData={blackBoxEventData} variant="primary" />
                    )}
                    <Badge className="mb-4 font-medium">Our science</Badge>
                    <Heading as="h2" size="sm" sizes={{ md: "md" }}>
                        How Oestra<sup>®</sup> supports <br className="md:hidden" />your <Heading as="i" weight="400" font="featureDisplay" className="text-[28px] md:text-[36px]">hormones</Heading>
                    </Heading>
                </Reveal>

                <div className="relative flex justify-center items-center mb-4 md:mb-8 lg:mb-4">
                    <div className="relative w-full max-w-[390px] md:max-w-[540px] lg:max-w-[662px] aspect-[390/225]">
                        <Image
                            src="https://res.cloudinary.com/dirscuuhp/image/upload/v1774431749/new_design/Oestra_Magnific_Precision_Upscale_V2_2026-02-27_06-39-45_1_l634nb.webp"
                            alt={creamImageAlt}
                            fill
                            sizes="1000px"
                            className="object-cover object-center"
                        />

                        <div className="absolute top-0 md:top-[50px] left-[50px]">
                            <PulseInfoButton
                                title="Progesterone"
                                description="Protects the uterine lining, promotes restful sleep, balances estradiol, and supports a calm nervous system."
                                size="medium"
                                tooltipVariant={tooltipVariant}
                            />
                        </div>
                        <div className="hidden md:block absolute top-[122px] right-[-70px]">
                            <PulseInfoButton
                                title="Estradiol"
                                description="Engineered for vaginal absorption to enhance bioavailability, maintain natural pH, prevent leakage, and deliver stable systemic hormone levels."
                                direction="reverse"
                                size="medium"
                                tooltipVariant={tooltipVariant}
                            />
                        </div>
                        <div className="md:hidden absolute top-[70px] left-[40px]">
                            <PulseInfoButton
                                title="Estradiol"
                                description="Engineered for vaginal absorption to enhance bioavailability, maintain natural pH, prevent leakage, and deliver stable systemic hormone levels."
                                size="medium"
                                tooltipVariant={tooltipVariant}
                            />
                        </div>
                        <div className="hidden md:block absolute top-[190px] left-[-40px]">
                            <PulseInfoButton
                                title={<>Ellagē<sup>®</sup> Base</>}
                                description="Supports brain, bones, metabolism, mood, vaginal tissue, and long-term vitality."
                                size="medium"
                                tooltipVariant={tooltipVariant}
                            />
                        </div>
                        <div className="md:hidden absolute top-[155px] left-[120px]">
                            <PulseInfoButton
                                title={<>Ellagē<sup>®</sup> Base</>}
                                description="Supports brain, bones, metabolism, mood, vaginal tissue, and long-term vitality."
                                size="medium"
                                direction="reverse"
                                tooltipVariant={tooltipVariant}
                            />
                        </div>
                    </div>
                </div>
                <div className="hidden lg:grid items-stretch grid-cols-3 gap-3">
                    {scienceHormones.map((hormone, index) => (
                        <div key={index} className={cn(index === 3 && "md:col-span-3")}>
                            <HormoneCard hormone={hormone} variant={index === 3 ? "wide" : "default"} eventData={hormoneCardEventData} />
                        </div>
                    ))}
                </div>
            </ElementorContainer>

            <div className="lg:hidden box-content overflow-hidden pl-6 pr-5">
                <Carousel
                    loop={false}
                    opts={{
                        align: "start",
                        skipSnaps: false,
                        dragFree: false
                    }}
                    variant="compact"
                    compactPaginationAlign="right"
                    compactPaginationBg="bg-pureWhite/10 backdrop-blur-[25px]"
                    compactPaginationDotColor="bg-gray100"
                    className="w-full"
                >
                    <CarouselContentVisible className="max-md:-ml-2">
                        {scienceHormones.map((hormone, index) => (
                            <CarouselItem key={index} className="max-md:pl-2 flex-shrink-0 basis-[min(100%,388px)] md:basis-[48%]">
                                <HormoneCard
                                    hormone={hormone}
                                    eventData={hormoneCardEventData}
                                />
                            </CarouselItem>
                        ))}
                    </CarouselContentVisible>
                </Carousel>
            </div>

            <div id="science-cards-section" />

            <div className={buttonWrapperClassName}>
                <QuizCTA
                    variant="primary"
                    eventData={{
                        event_name: GTM_EVENT_NAMES.LP_INTERACTION,
                        widget: "our_science",
                        meta_data: {
                            click_text: CTA_TITLE
                        },
                    }}
                    href={QUIZ_ROUTES.HORMONE_IMBALANCE}
                    size="md"
                    className="max-md:w-full"
                >
                    <span className="inline-block">
                        <TextWithRegisteredMark text={ctaTitle} />
                    </span>
                </QuizCTA>
            </div>
        </section>
    )
};

export default HormonesSection;
